import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ShieldCheck, AlertTriangle, ExternalLink, RefreshCw, Wallet, ArrowDownToLine,
  Edit2, X, Info,
} from 'lucide-react';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { facebookAdAccountService, AdAccountHealth, MetaAdAccount } from '@/services/facebook-ad-account.service';

interface Props {
  health: AdAccountHealth | null;
  account: MetaAdAccount | null;
  onAccountUpdate: (a: MetaAdAccount) => void;
  onHealthRefresh: (h: AdAccountHealth) => void;
}

/**
 * Convert Meta's "minor units" string (e.g. "50000" = $500.00) to a friendly
 * display string. Meta returns these as strings to avoid floating point.
 */
function fromMinor(s: string | undefined, currency: string | undefined): string {
  if (!s || s === '0') return '—';
  const n = parseFloat(s) / 100;
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim();
}

function isCapped(s: string | undefined): boolean {
  return !!s && s !== '0' && parseFloat(s) > 0;
}

/** Percent of the cap consumed by `amount_spent`. Null when no cap is set. */
function usagePct(spent?: string, cap?: string): number | null {
  if (!isCapped(cap)) return null;
  const sp = parseFloat(spent ?? '0');
  const cp = parseFloat(cap ?? '0');
  if (!Number.isFinite(sp) || !Number.isFinite(cp) || cp === 0) return null;
  return Math.min(100, Math.max(0, (sp / cp) * 100));
}

export const SpendCapPanel: React.FC<Props> = ({ health, account, onAccountUpdate, onHealthRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newCap, setNewCap] = useState('');
  const [resetPeriod, setResetPeriod] = useState<'lifetime' | 'monthly'>('lifetime');

  const currency = health?.currency ?? account?.currency ?? '';
  const spent = health?.amountSpent ?? account?.amountSpent;
  const balance = health?.balance ?? account?.balance;
  const cap = health?.spendCap ?? account?.spendCap;

  const pct = useMemo(() => usagePct(spent, cap), [spent, cap]);
  const isNearCap = pct !== null && pct >= 80;
  const isAtCap = pct !== null && pct >= 100;

  const startEdit = () => {
    if (isCapped(cap)) {
      // Pre-fill with current cap in major units.
      setNewCap((parseFloat(cap!) / 100).toString());
    } else {
      setNewCap('');
    }
    setEditing(true);
  };

  const save = async () => {
    const value = parseFloat(newCap);
    if (!Number.isFinite(value) || value < 0) { toast.error('Enter a non-negative amount'); return; }
    setBusy(true);
    try {
      const r = await facebookAdAccountService.setSpendCap({ spendCap: value, resetPeriod });
      onAccountUpdate(r.account);
      toast.success(value === 0 ? 'Spend cap cleared' : `Spend cap set to ${value} ${currency}`);
      setEditing(false);
    } catch {
      // axios interceptor toasts the backend message
    } finally { setBusy(false); }
  };

  const clearCap = async () => {
    if (!window.confirm('Remove the spend cap entirely? Meta will no longer pause delivery at any total amount.')) return;
    setBusy(true);
    try {
      const r = await facebookAdAccountService.setSpendCap({ spendCap: 0 });
      onAccountUpdate(r.account);
      toast.success('Spend cap cleared');
      setEditing(false);
    } catch {
      // axios interceptor toasts the backend message
    } finally { setBusy(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const h = await facebookAdAccountService.health(true);
      onHealthRefresh(h);
      toast.success('Refreshed from Meta');
    } catch {
      // axios interceptor toasts
    } finally { setRefreshing(false); }
  };

  return (
    <Card>
      <SectionHeader
        title="Spend cap & balance"
        className="mb-4"
        action={
          <Button size="sm" variant="ghost" onClick={refresh} loading={refreshing} iconLeft={<RefreshCw size={12} />}>
            Refresh
          </Button>
        }
      />

      {/* Top row: balance / spent / cap */}
      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        <Tile
          icon={<Wallet size={14} />}
          label="Account balance"
          value={fromMinor(balance, currency)}
          hint="Prepaid amount Meta will draw from."
        />
        <Tile
          icon={<ArrowDownToLine size={14} />}
          label="Lifetime spent"
          value={fromMinor(spent, currency)}
          hint="Total ever spent on this ad account."
        />
        <Tile
          icon={isCapped(cap) ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
          label="Account spend cap"
          value={isCapped(cap) ? fromMinor(cap, currency) : 'No cap set'}
          hint={isCapped(cap) ? 'Delivery pauses when reached.' : 'No safety net — delivery only stops when you pause.'}
          accent={isCapped(cap) ? 'success' : 'warning'}
        />
      </div>

      {/* Usage bar (only when capped) */}
      {pct !== null && (
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-1.5 text-xs">
            <span className="text-text-muted">Spend cap usage</span>
            <span className={clsx(
              'font-bold tabular-nums',
              isAtCap ? 'text-danger' : isNearCap ? 'text-warning' : 'text-text-primary',
            )}>
              {pct.toFixed(1)}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-alt">
            <div
              className={clsx(
                'h-full rounded-full transition-all',
                isAtCap ? 'bg-danger' : isNearCap ? 'bg-warning' : 'bg-success',
              )}
              style={{ width: `${pct}%` }}
            />
          </div>
          {isAtCap && (
            <p className="mt-1.5 text-2xs text-danger">
              Spend cap reached — Meta has paused delivery on this ad account. Raise the cap or clear it to resume.
            </p>
          )}
          {isNearCap && !isAtCap && (
            <p className="mt-1.5 text-2xs text-warning">
              Approaching the spend cap. Bump it up before delivery pauses.
            </p>
          )}
        </div>
      )}

      {/* Set / edit form */}
      {!editing ? (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface-alt/40 px-4 py-3">
          <div className="text-xs text-text-muted">
            Account-level cap is the hard ceiling — Meta pauses ALL campaigns once reached. The card itself and the <strong className="text-text-primary">billing threshold</strong> (when Meta auto-charges) live in Business Manager and are not editable from here.
          </div>
          <Button size="sm" variant="primary" onClick={startEdit} iconLeft={<Edit2 size={12} />}>
            {isCapped(cap) ? 'Edit cap' : 'Set cap'}
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border-strong bg-surface-alt p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label={`Cap amount (${currency || 'currency'})`} required hint={`Set 0 to clear the cap. Enter major units in ${currency || 'your ad account currency'}.`}>
              <Input
                type="number"
                min={0}
                step="any"
                placeholder="500"
                value={newCap}
                onChange={(e) => setNewCap(e.target.value)}
                autoFocus
              />
            </FormField>
            <FormField label="Reset period" hint="Lifetime caps are forever; monthly resets at month-start.">
              <Select value={resetPeriod} onChange={(e) => setResetPeriod(e.target.value as 'lifetime' | 'monthly')}>
                <option value="lifetime">Lifetime (no reset)</option>
                <option value="monthly">Monthly</option>
              </Select>
            </FormField>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
            <div className="flex items-center gap-2 text-2xs text-text-muted">
              <Info size={11} />
              Lifetime spent so far: <strong className="text-text-primary">{fromMinor(spent, currency)}</strong>
            </div>
            <div className="flex gap-2">
              {isCapped(cap) && (
                <Button size="sm" variant="ghost" onClick={clearCap} disabled={busy} iconLeft={<X size={12} />}>
                  Remove cap
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" loading={busy} onClick={save} disabled={busy || newCap === ''}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer link to Meta billing for things we can't expose */}
      <div className="mt-3 flex items-start gap-2 text-2xs text-text-muted">
        <Info size={11} className="mt-0.5 shrink-0" />
        <div>
          To change your <strong className="text-text-primary">payment method</strong> (card on file) or{' '}
          <strong className="text-text-primary">billing threshold</strong> (the auto-charge trigger amount), use Meta's Business Manager — these aren't exposed via the Marketing API.{' '}
          <a
            href="https://business.facebook.com/billing_hub/payment_settings"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Open Payment Settings <ExternalLink size={10} />
          </a>
        </div>
      </div>
    </Card>
  );
};

const Tile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  accent?: 'success' | 'warning' | 'danger';
}> = ({ icon, label, value, hint, accent }) => (
  <div className="rounded-xl border border-border bg-surface-alt/40 p-3">
    <div className={clsx(
      'flex items-center gap-1.5 mb-1 text-2xs uppercase tracking-wide',
      accent === 'success' ? 'text-success'
      : accent === 'warning' ? 'text-warning'
      : accent === 'danger' ? 'text-danger'
      : 'text-text-muted',
    )}>
      <span>{icon}</span>
      <span>{label}</span>
    </div>
    <div className="font-display text-lg font-bold text-text-primary tabular-nums">{value}</div>
    {hint && <div className="mt-1 text-2xs text-text-muted">{hint}</div>}
  </div>
);
